import { Button } from '@/components/shadcn/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/shadcn/popover'
import { cn } from '@/lib/utils'
import { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { IconCheckMarkOutline } from '@icons/IconCheckMarkOutline'
import { IconCloseOutline } from '@icons/IconCloseOutline'
import { IconDownChevronOutline } from '@icons/IconDownChevronOutline'
import { Badge } from '@/components/shadcn/badge'
// import { useVirtualizer } from '@tanstack/react-virtual'
import { BaseFieldProps } from '../types'
import clsx from 'clsx'

interface Option {
  label: string
  value: string
}

interface MultiLevelComboboxProps {
  options: Option[]
  value: string[]
  onChange: (value: string[]) => void
  placeholder?: string
  disabled?: boolean
  variant: BaseFieldProps['variant']
  // When rendering in a row, apply inward notches using clip-paths
  showLeftNotch?: boolean
  showRightNotch?: boolean
}

function selectionArraysEqual(left: string[], right: string[]) {
  if (left.length !== right.length) {
    return false
  }

  const sortedLeft = [...left].sort()
  const sortedRight = [...right].sort()

  return sortedLeft.every((value, index) => value === sortedRight[index])
}

export function MultiLevelCombobox({
  options = [],
  value = [],
  onChange,
  placeholder = 'Select options',
  disabled = false,
  variant = 'column',
  showLeftNotch = false,
  showRightNotch = false,
}: MultiLevelComboboxProps) {
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [draftValue, setDraftValue] = useState<string[]>([])
  const inputRef = useRef<HTMLInputElement>(null)
  const draftValueRef = useRef<string[]>([])
  // const listRef = useRef<HTMLDivElement>(null)
  const hasScrolledRef = useRef(false)

  // Ensure options and value are always arrays
  const safeOptions = useMemo(() => (Array.isArray(options) ? options : []), [options])
  const safeValue = useMemo(() => (Array.isArray(value) ? value : []), [value])
  const displayValue = open ? draftValue : safeValue

  const setDraftSelection = useCallback((nextValue: string[]) => {
    draftValueRef.current = nextValue
    setDraftValue(nextValue)
  }, [])

  useEffect(() => {
    if (!open) {
      setDraftSelection(safeValue)
    }
  }, [open, safeValue, setDraftSelection])

  // Keep draft aligned when the parent cascade resets this level while the popover is open.
  useEffect(() => {
    if (open) {
      setDraftSelection(safeValue)
    }
  }, [open, safeValue, setDraftSelection])

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        setDraftSelection(safeValue)
        setOpen(true)
        return
      }

      const pendingValue = draftValueRef.current
      if (!selectionArraysEqual(pendingValue, safeValue)) {
        onChange(pendingValue)
      }

      setOpen(false)
    },
    [onChange, safeValue, setDraftSelection]
  )

  // Get selected option labels
  const selectedLabels = useMemo(() => {
    return displayValue
      .map((val) => {
        const option = safeOptions.find((opt) => opt.value === val)
        return option ? option.label : null
      })
      .filter(Boolean) as string[]
  }, [displayValue, safeOptions])

  // Filter options based on search query
  const filteredOptions = useMemo(
    () =>
      searchQuery
        ? safeOptions.filter((option) => option.label.toLowerCase().includes(searchQuery.toLowerCase()))
        : safeOptions,
    [safeOptions, searchQuery]
  )

  // Set up virtualization
  // const rowVirtualizer = useVirtualizer({
  //   count: filteredOptions.length,
  //   getScrollElement: () => listRef.current,
  //   estimateSize: () => 36,
  //   overscan: 10,
  // })

  // Reset search and scroll state when dropdown closes
  useEffect(() => {
    if (!open) {
      setSearchQuery('')
      hasScrolledRef.current = false
    }
  }, [open])

  const handleSelect = useCallback(
    (optionValue: string) => {
      const currentValue = draftValueRef.current
      const isSelected = currentValue.includes(optionValue)
      const newValue = isSelected
        ? currentValue.filter((val) => val !== optionValue)
        : [...currentValue, optionValue]

      setDraftSelection(newValue)
    },
    [setDraftSelection]
  )

  const handleClear = useCallback(
    (e?: MouseEvent) => {
      e?.stopPropagation()
      e?.preventDefault()
      setDraftSelection([])
      onChange([])
      setOpen(false)
    },
    [onChange, setDraftSelection]
  )

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
    hasScrolledRef.current = false // Reset scroll state when search changes
  }, [])

  const selectAllTargetOptions = useMemo(
    () => (searchQuery.trim() ? filteredOptions : safeOptions),
    [searchQuery, filteredOptions, safeOptions]
  )
  const selectAllTargetValues = useMemo(() => selectAllTargetOptions.map((o) => o.value), [selectAllTargetOptions])
  const allTargetsSelected = useMemo(
    () => selectAllTargetValues.length > 0 && selectAllTargetValues.every((v) => displayValue.includes(v)),
    [displayValue, selectAllTargetValues]
  )

  const handleSelectAll = useCallback(() => {
    const merged = new Map(draftValueRef.current.map((v) => [v, v]))
    for (const v of selectAllTargetValues) {
      merged.set(v, v)
    }
    setDraftSelection(Array.from(merged.values()))
  }, [selectAllTargetValues, setDraftSelection])

  // Render a simple list of options for debugging
  const renderSimpleOptionsList = () => {
    return (
      <div className="max-h-[300px] overflow-auto p-2">
        {filteredOptions.length === 0 ? (
          <div className="py-6 text-center text-sm">No results found.</div>
        ) : (
          filteredOptions.map((option) => {
            const isSelected = displayValue.includes(option.value)

            return (
              <div
                key={option.value}
                className={cn(
                  'flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-muted',
                  isSelected && 'bg-muted'
                )}
                onClick={() => handleSelect(option.value)}
              >
                <IconCheckMarkOutline
                  className={cn('mr-2 h-4 w-4 shrink-0', isSelected ? 'opacity-100' : 'opacity-0')}
                />
                {option.label}
              </div>
            )
          })
        )}
      </div>
    )
  }

  return (
    <div className="relative w-full">
      <div className="flex w-full items-center">
        <div className="relative flex-1 overflow-visible">
          <Popover open={open} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild>
              <Button
                variant="input"
                role="combobox"
                aria-expanded={open}
                className={cn(
                  'w-full items-center justify-between',
                  displayValue.length === 0 && 'text-muted-foreground',
                  {
                    'h-6 min-h-0 min-w-48 px-2 py-0': variant === 'row',
                    'rounded-l-none border-l-0 pl-3': variant === 'row' && showLeftNotch,
                  }
                )}
                disabled={disabled}
              >
                <span className="flex min-w-0 flex-1 items-center truncate">
                  {displayValue.length > 0 ? (
                    selectedLabels.length > 2 ? (
                      <div className="inline-flex items-center gap-0.5">
                        {selectedLabels.slice(0, 2).map((label) => (
                          <Badge key={label} variant="outline" className="bg-muted">
                            <div className="inline-block w-20 truncate font-normal">{label}</div>
                          </Badge>
                        ))}
                        <Badge variant="outline">+ {selectedLabels.length - 2}</Badge>
                      </div>
                    ) : (
                      <div className="inline-flex items-center gap-2">
                        {selectedLabels.map((label) => (
                          <Badge variant="outline" key={label} className="bg-muted">
                            <div className="inline-block w-24 truncate font-normal">{label}</div>
                          </Badge>
                        ))}
                      </div>
                    )
                  ) : (
                    placeholder
                  )}
                </span>
                <span className={cn('ml-2 inline-flex items-center gap-1', variant === 'row' && 'pr-1')}>
                  <IconDownChevronOutline className="h-3 w-3 shrink-0 opacity-50" />
                  {variant === 'row' && displayValue.length > 0 && (
                    <span
                      role="button"
                      tabIndex={0}
                      onMouseDown={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                      }}
                      onClick={(e) => handleClear(e)}
                      aria-label="Clear selection"
                      className={clsx(
                        'z-20 flex h-[1.125rem] w-[1.125rem] items-center justify-center rounded-full bg-background text-muted-foreground hover:border-brand-hover hover:text-brand-hover',
                        variant === 'row' && 'absolute -right-2 -top-2 border'
                      )}
                    >
                      <IconCloseOutline size={10} className="opacity-60" />
                    </span>
                  )}
                </span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
              <div className="flex flex-col">
                <div className="flex items-center border-b px-3">
                  <input
                    ref={inputRef}
                    className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    autoFocus
                  />
                </div>

                {/* Use simple list rendering instead of virtualization for now */}
                {renderSimpleOptionsList()}

                {safeOptions.length > 0 && selectAllTargetValues.length > 0 && !allTargetsSelected && (
                  <div className="flex justify-end gap-x-2 border-t p-2">
                    <Button size="sm" variant="outline" className="h-6" onClick={handleSelectAll}>
                      <IconCheckMarkOutline className="mr-2 size-3" />
                      Select all
                    </Button>
                  </div>
                )}

                {/* Commented out virtualization for debugging
                {filteredOptions.length === 0 ? (
                  <div className="py-6 text-center text-sm">No results found.</div>
                ) : (
                  <div 
                    ref={listRef} 
                    className="max-h-[300px] overflow-auto"
                  >
                    <div
                      style={{
                        height: `${rowVirtualizer.getTotalSize()}px`,
                        width: '100%',
                        position: 'relative',
                      }}
                    >
                      {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                        const option = filteredOptions[virtualRow.index]
                        if (!option) {
                          return null;
                        }

                        const isSelected = safeValue.includes(option.value)

                        return (
                          <div
                            key={option.value}
                            data-index={virtualRow.index}
                            className={cn(
                              'absolute left-0 top-0 w-full',
                              'flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-grey-lightest',
                              isSelected && 'bg-grey-lightest'
                            )}
                            style={{
                              height: `${virtualRow.size}px`,
                              transform: `translateY(${virtualRow.start}px)`,
                            }}
                            onClick={() => handleSelect(option.value)}
                          >
                            <IconCheckMarkOutline
                              className={cn('mr-2 h-4 w-4', isSelected ? 'opacity-100' : 'opacity-0')}
                            />
                            {option.label}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
                */}
              </div>
            </PopoverContent>
          </Popover>
          {/* {variant === 'row' && showRightNotch && (
            <span
              aria-hidden
              className="pointer-events-none absolute right-[-8px] top-1/2 z-10 h-4 w-4 -translate-y-1/2 rotate-45 border border-border bg-offwhite"
            />
          )} */}
        </div>
        {/* Clear button next to trigger for column variant only */}
        {variant !== 'row' && displayValue.length > 0 && (
          <Button
            variant="ghost"
            size="icon"
            className={cn('ml-1 h-9 w-9 p-0')}
            onClick={() => handleClear()}
            aria-label="Clear selection"
            disabled={disabled}
          >
            <IconCloseOutline size={12} className="shrink-0 opacity-50 hover:opacity-100" />
          </Button>
        )}
      </div>
    </div>
  )
}
